/* Copyright (c) 2018 Backpack Health LLC */
import * as React from 'react';
import classnames from 'classnames';

import { rgb2css, rgba2rgb } from '../../../../../utilities/types';
import ContentFrame from '../../../../applicationFrame/ContentFrame';
import {
  ScrollAwareContext,
  ScrollAwareItem,
  ScrollAwareScroller,
} from '../../../../components/ScrollAware';
import AppNavLeftRightNavigation from '../../../../components/appNav/appNavBits/AppNavLeftRightNavigation';
import { ProgressIndicator } from '../../components/ProgressIndicator';
import { IconPause } from '../../../../components/icons/SvgIcons';
import AppNavHeaderTitle from '../../../../components/appNav/appNavBits/AppNavHeaderTitle';
import AppHeaderScrollable from '../../../../components/appNav/AppHeaderScrollable';
import RouteNames from '../../../../appAuthorized/RouteNames';
import { updateStatusBarColor } from '../../../../../utilities/MobileUtil';

import './SurveyPageLayout.styles.scss';
import AppUtils from '../../../../../utilities/AppUtils';
import UI_CONSTANTS from '../../../../../constants/uiConstants';
import { useUIState } from '../../../../hooks/useUIState';
import { useGroupForActiveProfile } from '../../../../hooks/useGroup';
import { useNavigate } from '../../../../hooks/useNavigate';

const DISPLAY_TITLE_HEIGHT = 30;

interface Props {
  className: string;
  navButtons: any;
  groupId: number;
  fadeHeight?: string;
  children: any;
  title?: React.ReactNode;
  mainTitle?: React.ReactNode;
  progress?: React.ReactNode;
  progressPercentage?: number;
  submitDisabled?: boolean;
  onDisabledClick?(): void;
  extendedHeader?(theme): any;
}

const calculateBottomBorderOffset = (hasStickyTitle) => {
  let borderOffsetPos = 0;
  if (hasStickyTitle) {
    borderOffsetPos += 30;
  }
  if (AppUtils.isWideView()) {
    borderOffsetPos += 64;
  } else {
    borderOffsetPos += 55;
  }

  return borderOffsetPos + UI_CONSTANTS.statusBarHeight;
};

export const SurveyPageLayout = (props: Props) => {
  const { navButtons, groupId, children, title, progressPercentage, className, submitDisabled, onDisabledClick, extendedHeader, fadeHeight } = props;
  const { progress = <div><ProgressControl percentage={progressPercentage} groupId={groupId} /></div> } = props;
  const { mainTitle = title } = props;
  const [uiState] = useUIState();
  const [{ group }] = useGroupForActiveProfile(groupId);

  const theme = {
    base_color: rgba2rgb({ ...group.theme.base_rgba, a: 0.05 }),
    accent_color: rgba2rgb({ ...group.theme.accent_rgba, a: 0.1 }),
  };
  updateStatusBarColor(theme.base_color);

  const hasStickyTitle = !!title;
  const borderOffsetPos = navButtons ? calculateBottomBorderOffset(hasStickyTitle) : 56;
  const isWide = uiState.isWideDesktop;

  return (
    <div className={`cmp-survey-page-layout ${className}`}>
      <ScrollAwareContext extendedDisplayTitleHeight={DISPLAY_TITLE_HEIGHT}>
        {
          navButtons &&
          <SurveyQuestionAppNavStep
            color="gsMedDark"
            isNav={true}
            className=""
            style={{ backgroundColor: rgb2css(theme.base_color) }}
            progress={progress}
            submitDisabled={submitDisabled}
            onDisabledClick={onDisabledClick}
            {...navButtons}
          />
        }

        <ContentFrame>
          <ScrollAwareScroller>
            {
              isWide ? (
                <ScrollAwareItem isSticky>
                  {
                    mainTitle && (
                      <div className="cmp-survey-page-layout-extendedheader util-application-frame-forcards-padding" style={{ paddingBottom: 18, backgroundColor: rgb2css(theme.base_color) }}>
                        <div className="font-title-2 theme-group base color" style={{ paddingTop: 8 }}>
                          {mainTitle}
                        </div>
                      </div>
                    )
                  }
                  {
                    extendedHeader && (
                      <div className="cmp-survey-page-layout-extendedheader util-application-frame-forcards-padding" style={{ paddingBottom: 18, backgroundColor: rgb2css(theme.base_color) }}>
                        <div className="theme-group base color">
                          {extendedHeader(theme)}
                        </div>
                      </div>
                    )
                  }
                  <div className="cmp-survey-page-layout-sticky-border theme-group base background trans-10" />
                </ScrollAwareItem>
              ) : (
                hasStickyTitle ? (
                  <ScrollAwareItem isSticky>
                    {({ displayTitle }) => {
                      return displayTitle ? (
                        <AppNavHeaderTitle>
                          <div className="theme-group base color" style={{ backgroundColor: rgb2css(theme.base_color), height: DISPLAY_TITLE_HEIGHT, position: 'absolute', width: '100%', paddingTop: navButtons ? null : 14 }}>
                            {title}
                          </div>
                        </AppNavHeaderTitle>
                      ) : null;
                    }}
                  </ScrollAwareItem>
                ) : null
              )
            }

            {
              isWide ? null : (
                mainTitle ? (
                  <>
                    <AppHeaderScrollable
                      title={
                        progress
                        ? (<span className="theme-group base color" style={{ fontWeight: 'bold' }}>{mainTitle}</span>)
                        : null
                      }
                      colorThemeName="none"
                      colorTheme={theme}
                      fadeHeight={fadeHeight}
                      className={extendedHeader ? null : 'theme-group base border trans-10'}
                      style={{ backgroundColor: rgb2css(theme.base_color), borderBottom: (extendedHeader ? null : '1px solid') }}
                    >
                      <div className="font-title-2 theme-group base color" style={{ paddingTop: 8 }}>
                        {mainTitle}
                      </div>
                    </AppHeaderScrollable>
                  </>
                ) : null
              )
            }

            <div className="cmp-survey-page-layout-spacer">
              {
                isWide ? null : (
                  <>
                    {extendedHeader && (
                      <>
                        <div className="cmp-survey-page-layout-extendedheader util-application-frame-forcards-padding" style={{ paddingBottom: 18, backgroundColor: rgb2css(theme.base_color) }}>
                          <div className="theme-group base color">
                            {extendedHeader(theme)}
                          </div>
                        </div>
                      </>
                    )}
                    <div className={classnames('cmp-survey-page-layout-sticky-border theme-group base background trans-10', { 'is-sticky': true })} style={{ top: borderOffsetPos }} />
                  </>
                )
              }
              {typeof children === 'function' ? children(theme) : children}
            </div>
          </ScrollAwareScroller>
        </ContentFrame>
      </ScrollAwareContext>
    </div>
  );
};

const SurveyQuestionAppNavStep = ({ progress, ...otherProps }) => (
  <ScrollAwareItem isSticky stickyPosition={0} isNav>
    <AppNavLeftRightNavigation {...otherProps}>
      <AppNavHeaderTitle>
        {progress}
      </AppNavHeaderTitle>
    </AppNavLeftRightNavigation>
  </ScrollAwareItem>
);

const ProgressControl = ({ percentage, groupId }) => {
  const [, navActions] = useNavigate();

  return (
    <div className="cmp-survey-progress-control" style={{ width: 132, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div
        style={{ marginRight: 8, cursor: 'pointer' }}
        onClick={() => navActions.goTo(RouteNames.surveys(groupId))}
      >
        <IconPause
          fillClassName="theme-group base fill"
          borderClassName="theme-group base border"
        />
      </div>

      <ProgressIndicator percentage={percentage}/>
    </div>
  );
};
